<template>
  <f7-page name="group-members-import" @page:init="onInit">
    <f7-navbar :title="$t('groupImport.title')" back-link>
      <f7-nav-right v-if="step === 'SHARE'">
        <f7-link @click="resetAll">{{
          $t("groupImport.importAnother")
        }}</f7-link>
      </f7-nav-right>
    </f7-navbar>
    <AppLayout>
      <template #main>
        <!-- STEP 1: SOURCE -->
        <template v-if="step === 'SOURCE'">
          <f7-block>
            <p class="text-color-gray">{{ $t("groupImport.subtitle") }}</p>
            <f7-segmented strong round >
              <f7-button round :active="source === 'FILE'" @click="source = 'FILE'">
                {{ $t("groupImport.sourceFile") }}
              </f7-button>
              <f7-button round
                :active="source === 'MANUAL'"
                @click="source = 'MANUAL'"
              >
                {{ $t("groupImport.sourceManual") }}
              </f7-button>
            </f7-segmented>
          </f7-block>

          <!-- FILE SOURCE -->
          <f7-block v-if="source === 'FILE'">
            <h3>{{ $t("groupImport.uploadTitle") }}</h3>
            <p class="text-color-gray">{{ $t("groupImport.uploadHint") }}</p>

            <f7-block strong inset>
              <f7-button outline @click="downloadTemplate">
                {{ $t("groupImport.downloadTemplate") }}
              </f7-button>

              <div class="upload-zone">
                <f7-icon f7="doc_text" size="36" color="blue" />
                <strong>{{ $t("groupImport.chooseFile") }}</strong>
                <input
                  ref="fileInput"
                  class="hidden-input"
                  type="file"
                  accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                  @change="onFileSelected"
                />
                <f7-button fill @click="fileInput?.click()">
                  {{ $t("groupImport.chooseFile") }}
                </f7-button>
              </div>

              <p v-if="fileName" class="selected-file">
                {{ $t("groupImport.selectedFile", { name: fileName }) }}
              </p>

              <f7-list v-if="sheetNames.length > 1" strong inset>
                <f7-list-item :title="$t('groupImport.worksheet')">
                  <template #input>
                    <f7-input
                      v-model="selectedSheet"
                      type="select"
                      @change="readSelectedWorksheet"
                    >
                      <option
                        v-for="sheet in sheetNames"
                        :key="sheet"
                        :value="sheet"
                      >
                        {{ sheet }}
                      </option>
                    </f7-input>
                  </template>
                </f7-list-item>
              </f7-list>

              <p v-if="parseError" class="text-color-red">{{ parseError }}</p>
            </f7-block>
          </f7-block>

          <!-- MANUAL SOURCE -->
          <f7-block v-if="source === 'MANUAL'">
            <h3>{{ $t("groupImport.manualTitle") }}</h3>
            <p class="text-color-gray">{{ $t("groupImport.manualHint") }}</p>

            <ManualMemberInput
              v-for="(member, index) in manualMembers"
              :key="member.clientId"
              v-model="manualMembers[index]"
              :index="index"
              :is-single="manualMembers.length === 1"
              @remove="removeManualMember(index)"
            />

            <f7-block class="display-flex justify-content-space-between">
              <f7-button v-if="canUseContacts" small outline @click="addFromContacts">
                {{ $t("groupImport.addFromContacts") }}
              </f7-button>
              <f7-button small outline @click="addManualMember">
                {{ $t("groupImport.addAnother") }}
              </f7-button>
              <f7-button small fill @click="addManualToPreview">
                {{ $t("groupImport.addToList") }}
              </f7-button>
            </f7-block>
          </f7-block>
        </template>

        <!-- STEP 2: COLUMN MAPPING -->
        <f7-block v-else-if="step === 'MAP'">
          <h2>{{ $t("groupImport.mapTitle") }}</h2>
          <p class="text-color-gray">{{ $t("groupImport.mapHint") }}</p>

          <f7-list strong inset>
            <f7-list-item :title="$t('groupImport.firstName')" required>
              <template #input>
                <f7-input v-model="mapping.firstName" type="select">
                  <option value="">{{ $t("groupImport.selectColumn") }}</option>
                  <option
                    v-for="header in headers"
                    :key="header"
                    :value="header"
                  >
                    {{ header }}
                  </option>
                </f7-input>
              </template>
            </f7-list-item>
            <f7-list-item :title="$t('groupImport.lastName')" required>
              <template #input>
                <f7-input v-model="mapping.lastName" type="select">
                  <option value="">{{ $t("groupImport.selectColumn") }}</option>
                  <option
                    v-for="header in headers"
                    :key="header"
                    :value="header"
                  >
                    {{ header }}
                  </option>
                </f7-input>
              </template>
            </f7-list-item>
            <f7-list-item :title="$t('groupImport.email')">
              <template #input>
                <f7-input v-model="mapping.email" type="select">
                  <option value="">{{ $t("groupImport.selectColumn") }}</option>
                  <option
                    v-for="header in headers"
                    :key="header"
                    :value="header"
                  >
                    {{ header }}
                  </option>
                </f7-input>
              </template>
            </f7-list-item>
            <f7-list-item :title="$t('groupImport.phoneNumber')">
              <template #input>
                <f7-input v-model="mapping.phoneNumber" type="select">
                  <option value="">
                    {{ $t("groupImport.noPhoneColumn") }}
                  </option>
                  <option
                    v-for="header in headers"
                    :key="header"
                    :value="header"
                  >
                    {{ header }}
                  </option>
                </f7-input>
              </template>
            </f7-list-item>
          </f7-list>

          <f7-block class="display-flex justify-content-space-between">
            <f7-button outline @click="goSource">{{
              $t("groupImport.back")
            }}</f7-button>
            <f7-button
              fill
              :disabled="!hasRequiredMapping"
              @click="buildPreview"
            >
              {{ $t("groupImport.previewMembers") }}
            </f7-button>
          </f7-block>
        </f7-block>

        <!-- STEP 3: PREVIEW -->
        <f7-block v-else-if="step === 'PREVIEW'">
          <h2>{{ $t("groupImport.reviewTitle") }}</h2>

          <f7-card>
            <f7-card-content>
              <div class="import-summary">
                <div>
                  <strong>{{ previewRows.length }}</strong>
                  <span>{{ $t("groupImport.totalRows") }}</span>
                </div>
                <div class="text-color-green">
                  <strong>{{ validPreviewRows.length }}</strong>
                  <span>{{ $t("groupImport.ready") }}</span>
                </div>
                <div class="text-color-red">
                  <strong>{{ invalidPreviewRows.length }}</strong>
                  <span>{{ $t("groupImport.needAttention") }}</span>
                </div>
              </div>
            </f7-card-content>
          </f7-card>

          <f7-list strong inset>
            <f7-list-item
              v-for="row in visiblePreviewRows"
              :key="row.clientId"
              :title="rowTitle(row)"
              :subtitle="
                row.email || row.phoneNumber || $t('groupImport.noContact')
              "
            >
              <template #after>
                <f7-badge :color="row.valid ? 'green' : 'red'">
                  {{
                    row.valid
                      ? $t("groupImport.ready")
                      : $t("groupImport.needAttention")
                  }}
                </f7-badge>
              </template>
              <template #footer>
                <span v-if="row.valid">{{ row.phoneNumber || row.email }}</span>
                <span v-else class="text-color-red">{{
                  row.errors.join(", ")
                }}</span>
              </template>
            </f7-list-item>
            <f7-list-item
              v-if="!previewRows.length"
              title=" "
              :subtitle="$t('groupImport.emptyState')"
            />
          </f7-list>

          <f7-block v-if="previewRows.length > previewLimit">
            <f7-button outline @click="showAllPreview = !showAllPreview">
              {{
                showAllPreview
                  ? $t("groupImport.showLess")
                  : $t("groupImport.showAll", { count: previewRows.length })
              }}
            </f7-button>
          </f7-block>

          <f7-block class="display-flex justify-content-space-between">
            <f7-button outline @click="goSource">{{
              $t("groupImport.back")
            }}</f7-button>
            <f7-button
              fill
              :disabled="!validPreviewRows.length || importing"
              @click="doImport"
            >
              {{
                importing
                  ? $t("groupImport.importing")
                  : $t("groupImport.import", { count: validPreviewRows.length })
              }}
            </f7-button>
          </f7-block>

          <f7-block v-if="invalidPreviewRows.length" class="text-color-gray">
            {{ $t("groupImport.invalidNote") }}
          </f7-block>
        </f7-block>

        <!-- STEP 4: RESULT -->
        <f7-block v-else-if="step === 'RESULT' && importResult">
          <h2>{{ $t("groupImport.importTitle") }}</h2>

          <f7-card>
            <f7-card-content>
              <div class="import-summary">
                <div>
                  <strong>{{ importResult.total }}</strong>
                  <span>{{ $t("groupImport.processed") }}</span>
                </div>
                <div class="text-color-green">
                  <strong>{{ importResult.ready }}</strong>
                  <span>{{ $t("groupImport.readyToInvite") }}</span>
                </div>
                <div v-if="importResult.invalid" class="text-color-red">
                  <strong>{{ importResult.invalid }}</strong>
                  <span>{{ $t("groupImport.invalid") }}</span>
                </div>
                <div v-if="importResult.duplicates" class="text-color-orange">
                  <strong>{{ importResult.duplicates }}</strong>
                  <span>{{ $t("groupImport.duplicates") }}</span>
                </div>
              </div>
            </f7-card-content>
          </f7-card>

          <f7-block v-if="importResult.members.length">
            <f7-block-title>{{
              $t("groupImport.resultDetails")
            }}</f7-block-title>
            <f7-list strong inset>
              <f7-list-item
                v-for="member in importResult.members"
                :key="member.id"
                :title="
                  `${member.firstName || ''} ${member.lastName || ''}`.trim()
                "
                :subtitle="member.email || member.phoneNumber"
              >
                <template #after>
                  <f7-badge :color="statusColor(member.status)">{{
                    statusLabel(member.status)
                  }}</f7-badge>
                </template>
                <template #footer>
                  <span v-if="member.validationError" class="text-color-red">{{
                    member.validationError
                  }}</span>
                </template>
              </f7-list-item>
            </f7-list>
          </f7-block>

          <f7-block>
            <f7-button
              fill
              large
              :disabled="!readyMembers.length"
              @click="goSend"
            >
              {{ $t("groupImport.reviewAndSend") }}
            </f7-button>
            <f7-button outline large class="margin-top" @click="resetAll">
              {{ $t("groupImport.importAnother") }}
            </f7-button>
          </f7-block>
        </f7-block>

        <!-- STEP 5: SEND -->
        <f7-block v-else-if="step === 'SEND'">
          <h2>{{ $t("groupImport.sendTitle") }}</h2>

          <f7-block strong inset>
            <div class="invite-count">
              {{
                $t("groupImport.selectedCount", {
                  selected: selectedIds.length,
                  total: readyMembers.length,
                })
              }}
            </div>

            <f7-list>
              <f7-list-item
                v-for="member in readyMembers"
                :key="member.id"
                checkbox
                :checked="selectedIds.includes(member.id)"
                :title="
                  `${member.firstName || ''} ${member.lastName || ''}`.trim()
                "
                :footer="member.email || member.phoneNumber"
                @change="toggleMember(member.id)"
              />
            </f7-list>
          </f7-block>

          <f7-list form inset>
            <f7-list-input
              v-model="customMessage"
              :label="$t('groupImport.welcomeMessage')"
              type="textarea"
              :placeholder="$t('groupImport.welcomePlaceholder')"
            />
            <f7-list-input
              v-model.number="expiresInDays"
              :label="$t('groupImport.expiresAfter')"
              type="number"
              :placeholder="'14'"
            />
          </f7-list>

          <f7-block class="display-flex justify-content-space-between">
            <f7-button outline @click="step = 'RESULT'">{{
              $t("groupImport.back")
            }}</f7-button>
            <f7-button
              fill
              large
              :disabled="!selectedIds.length || sending"
              @click="doSend"
            >
              {{
                sending
                  ? $t("groupImport.sending")
                  : $t("groupImport.send", { count: selectedIds.length })
              }}
            </f7-button>
          </f7-block>
        </f7-block>

        <!-- STEP 6: SHARE -->
        <f7-block v-else-if="step === 'SHARE'">
          <h2>{{ $t("groupImport.shareTitle") }}</h2>
          <p class="text-color-gray">{{ $t("groupImport.shareHint") }}</p>

          <f7-list strong inset>
            <f7-list-item
              v-for="inv in sentInvitations"
              :key="inv.id"
              :title="invitationName(inv)"
              :subtitle="inv.email || inv.phoneNumber"
            >
              <template #footer>
                <div class="share-actions">
                  <f7-button
                    v-if="inv.phoneNumber"
                    small
                    fill
                    color="green"
                    @click="openWhatsApp(inv)"
                  >
                    <f7-icon f7="logo_whatsapp" size="14" />
                    {{ $t("groupImport.shareWhatsApp") }}
                  </f7-button>
                  <f7-button small outline @click="shareInvitation(inv)">
                    {{ $t("groupImport.share") }}
                  </f7-button>
                  <f7-button small outline @click="copyInvitationLink(inv)">
                    {{ $t("groupImport.copyLink") }}
                  </f7-button>
                </div>
              </template>
            </f7-list-item>
          </f7-list>

          <f7-block v-if="sentInvitations.length">
            <f7-button outline @click="copyAllLinks">
              <f7-icon f7="square_on_square" size="16" />
              {{ $t("groupImport.copyAllLinks") }}
            </f7-button>
          </f7-block>

          <f7-block v-if="phoneInvitations.length">
            <f7-block-title>{{ $t("groupImport.bulkTitle") }}</f7-block-title>
            <p class="text-color-gray">{{ $t("groupImport.bulkHint") }}</p>
            <f7-block strong inset>
              <pre class="bulk-message">{{ bulkMessage }}</pre>
              <f7-button fill small @click="copyBulkMessage">
                {{ $t("groupImport.copyBulkMessage") }}
              </f7-button>
            </f7-block>
          </f7-block>
        </f7-block>
      </template>
    </AppLayout>
  </f7-page>
</template>

<script setup>
import { computed, reactive, ref } from "vue";
import Papa from "papaparse";
import AppLayout from "../components/common/AppLayout.vue";
import * as XLSX from "xlsx";
import { f7 } from "framework7-vue";
import { useMembersStore } from "../js/pinia-stores/members.js";
import { useGroupsStore } from "../js/pinia-stores/group.js";
import { useSocialShare } from "../js/composables/useSocialShare.js";
import { useContactPicker } from "../js/composables/useContactPicker.js";
import { useCordovaPlugin } from "../js/composables/useCordovaPlugin.js";
import ManualMemberInput from "../components/ManualMemberInput.vue";
import i18n from "../js/i18n.js";
const { t } = i18n.global;

const { openContactPicker, count: contactCount } = useContactPicker();
const { getSelectedContactsForInvitation, canUseContacts } = useCordovaPlugin();

const props = defineProps({
  groupId: { type: String, required: true },
});

const membersStore = useMembersStore();
const groupsStore = useGroupsStore();
const { socialShare } = useSocialShare();

const previewLimit = 20;
const step = ref("SOURCE");
const source = ref("FILE");
const fileInput = ref(null);
const fileName = ref("");
const parseError = ref("");
const importing = ref(false);
const sending = ref(false);
const headers = ref([]);
const sourceRows = ref([]);
const sheetNames = ref([]);
const selectedSheet = ref("");
const previewRows = ref([]);
const showAllPreview = ref(false);
const workbook = ref(null);
const importResult = ref(null);
const selectedIds = ref([]);
const customMessage = ref("");
const expiresInDays = ref(14);
const sentInvitations = ref([]);
const groupName = ref("");

const mapping = reactive({
  firstName: "",
  lastName: "",
  email: "",
  phoneNumber: "",
});

const manualMembers = ref([createManualMember()]);

const hasRequiredMapping = computed(() =>
  Boolean(mapping.firstName && mapping.lastName),
);

const validPreviewRows = computed(() =>
  previewRows.value.filter((r) => r.valid),
);
const invalidPreviewRows = computed(() =>
  previewRows.value.filter((r) => !r.valid),
);
const visiblePreviewRows = computed(() =>
  showAllPreview.value
    ? previewRows.value
    : previewRows.value.slice(0, previewLimit),
);

const readyMembers = computed(() =>
  (importResult.value?.members || []).filter((m) => m.status === "READY"),
);

const phoneInvitations = computed(() =>
  sentInvitations.value.filter((inv) => inv.phoneNumber),
);

const bulkMessage = computed(() => {
  const intro =
    customMessage.value.trim() ||
    t("groupImport.defaultMessage", {
      group: groupName.value || t("groupImport.yourGroup"),
    });
  return phoneInvitations.value
    .map((inv) => {
      const name = invitationName(inv);
      return `${intro}${name ? ` (${name})` : ""}\n${inv.invitationUrl}`;
    })
    .join("\n\n");
});

async function onInit() {
  try {
    await groupsStore.fetchGroupDetail(props.groupId);
    groupName.value = groupsStore.activeGroup?.name || "";
  } catch {
    // group name optional
  }
}

function createManualMember() {
  return {
    clientId: crypto.randomUUID(),
    firstName: "",
    lastName: "",
    phoneNumber: "",
    email: "",
    errors: [],
  };
}

function addManualMember() {
  manualMembers.value.push(createManualMember());
}

async function addFromContacts() {
  const selected = await openContactPicker({
    multiple: true,
    title: t("groupImport.selectFromContacts"),
  });

  (selected || []).forEach((contact) => {
    const member = createManualMember();
    member.firstName = contact.firstName || "";
    member.lastName = contact.lastName || "";
    member.phoneNumber = contact.phonesSingle || "";
    member.email = contact.emailsSingle || "";
    manualMembers.value.push(member);
  });
}

function removeManualMember(index) {
  manualMembers.value.splice(index, 1);
}

function addManualToPreview() {
  const added = [];
  manualMembers.value.forEach((member) => {
    if (
      !member.firstName.trim() &&
      !member.lastName.trim() &&
      !member.phoneNumber.trim() &&
      !member.email.trim()
    )
      return;
    const row = buildRow(
      member.firstName,
      member.lastName,
      member.phoneNumber,
      member.email,
    );
    previewRows.value.push(row);
    added.push(row);
  });
  if (!added.length) {
    f7.toast.show({ text: t("groupImport.emptyManual"), closeTimeout: 2000 });
    return;
  }
  manualMembers.value = [createManualMember()];
  step.value = "PREVIEW";
}

function onFileSelected(event) {
  const file = event.target.files?.[0];
  event.target.value = null;
  if (!file) return;

  resetParseState();
  fileName.value = file.name;

  try {
    if (file.name.toLowerCase().endsWith(".csv")) {
      parseCsvFile(file);
    } else {
      parseExcelFile(file);
    }
  } catch (error) {
    parseError.value = error?.message || t("groupImport.parseError");
  }
}

function parseCsvFile(file) {
  Papa.parse(file, {
    header: true,
    skipEmptyLines: "greedy",
    transformHeader: (header) => header.trim(),
    complete(results) {
      if (results.errors?.length) {
        parseError.value = results.errors[0].message;
        return;
      }
      headers.value = results.meta.fields || [];
      sourceRows.value = results.data;
      if (!headers.value.length || !sourceRows.value.length) {
        parseError.value = t("groupImport.emptyFile");
        return;
      }
      autoMapColumns();
      step.value = "MAP";
    },
    error(error) {
      parseError.value = error?.message || t("groupImport.parseError");
    },
  });
}

async function parseExcelFile(file) {
  const buffer = await file.arrayBuffer();
  const parsed = XLSX.read(buffer, { type: "array", cellDates: true });
  workbook.value = parsed;
  sheetNames.value = parsed.SheetNames;
  if (!sheetNames.value.length) throw new Error(t("groupImport.emptyFile"));
  selectedSheet.value = sheetNames.value[0];
  readSelectedWorksheet();
}

function readSelectedWorksheet() {
  const worksheet = workbook.value?.Sheets[selectedSheet.value];
  if (!worksheet) return;
  const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "", raw: false });
  if (!rows.length) {
    parseError.value = t("groupImport.emptyFile");
    return;
  }
  headers.value = Object.keys(rows[0]);
  sourceRows.value = rows;
  autoMapColumns();
  step.value = "MAP";
}

function autoMapColumns() {
  mapping.firstName = findHeader([
    "firstname",
    "first name",
    "given name",
    "forename",
    "prenom",
    "prénom",
    "first_name",
  ]);
  mapping.lastName = findHeader([
    "lastname",
    "last name",
    "surname",
    "family name",
    "nom",
    "last_name",
  ]);
  mapping.email = findHeader(["email", "email address", "e-mail", "mail"]);
  mapping.phoneNumber = findHeader([
    "phone",
    "phone number",
    "phonenumber",
    "mobile",
    "mobile number",
    "telephone",
    "tel",
  ]);
}

function findHeader(aliases) {
  return (
    headers.value.find((header) => aliases.includes(normalizeHeader(header))) ||
    ""
  );
}

function normalizeHeader(value) {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/[_-]/g, " ")
    .replace(/\s+/g, " ");
}

function buildPreview() {
  previewRows.value = sourceRows.value.map((row, index) =>
    buildRow(
      row[mapping.firstName],
      row[mapping.lastName],
      mapping.phoneNumber ? row[mapping.phoneNumber] : "",
      mapping.email ? row[mapping.email] : "",
    ),
  );
  step.value = "PREVIEW";
}

function buildRow(firstName, lastName, phoneNumber, email) {
  const member = {
    clientId: crypto.randomUUID(),
    firstName: clean(firstName),
    lastName: clean(lastName),
    email: clean(email).toLowerCase(),
    phoneNumber: clean(phoneNumber),
  };
  const errors = validateMember(member);
  return { ...member, errors, valid: errors.length === 0 };
}

function validateMember(member) {
  const errors = [];
  if (!member.firstName)
    errors.push(t("groupImport.validation.firstNameRequired"));
  if (!member.lastName)
    errors.push(t("groupImport.validation.lastNameRequired"));
  if (!member.email && !member.phoneNumber)
    errors.push(t("groupImport.validation.contactRequired"));
  if (member.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(member.email)) {
    errors.push(t("groupImport.validation.invalidEmail"));
  }
  if (member.phoneNumber && !isReasonablePhone(member.phoneNumber)) {
    errors.push(t("groupImport.validation.invalidPhone"));
  }
  return errors;
}

function isReasonablePhone(value) {
  const cleaned = String(value).replace(/[\s()\-.]/g, "");
  if (cleaned.startsWith("+")) return /^\+[0-9]{7,15}$/.test(cleaned);
  return /^[0-9]{7,15}$/.test(cleaned);
}

function clean(value) {
  return String(value ?? "").trim();
}

function rowTitle(row) {
  const name = `${row.firstName} ${row.lastName}`.trim();
  return name || t("groupImport.noName");
}

async function doImport() {
  importing.value = true;
  try {
    const payload = validPreviewRows.value.map((row) => ({
      firstName: row.firstName,
      lastName: row.lastName,
      phoneNumber: row.phoneNumber || null,
      email: row.email || null,
    }));
    importResult.value = await membersStore.importMembers(
      props.groupId,
      payload,
    );
    step.value = "RESULT";
  } catch (error) {
    f7.dialog.alert(
      error?.response?.data?.message || t("groupImport.errorTitle"),
    );
  } finally {
    importing.value = false;
  }
}

function goSend() {
  selectedIds.value = readyMembers.value.map((m) => m.id);
  step.value = "SEND";
}

function toggleMember(id) {
  selectedIds.value = selectedIds.value.includes(id)
    ? selectedIds.value.filter((current) => current !== id)
    : [...selectedIds.value, id];
}

async function doSend() {
  sending.value = true;
  try {
    sentInvitations.value = await membersStore.sendInvitations(props.groupId, {
      importedMemberIds: selectedIds.value,
      customMessage: customMessage.value,
      expiresInDays: expiresInDays.value,
    });
    f7.toast.show({ text: t("groupImport.sentToast"), closeTimeout: 2000 });
    step.value = "SHARE";
  } catch (error) {
    f7.dialog.alert(
      error?.response?.data?.message || t("groupImport.errorSend"),
    );
  } finally {
    sending.value = false;
  }
}

function statusColor(status) {
  const map = {
    READY: "green",
    INVALID: "red",
    DUPLICATE: "orange",
    INVITED: "blue",
    ACCEPTED: "green",
    EXPIRED: "gray",
  };
  return map[status] || "gray";
}

function statusLabel(status) {
  const key = `groupImport.status.${status}`;
  return t(key) !== key ? t(key) : status;
}

function invitationName(inv) {
  const imported = (importResult.value?.members || []).find(
    (m) => m.id === inv.importedMemberId,
  );
  if (imported)
    return (
      `${imported.firstName || ""} ${imported.lastName || ""}`.trim() ||
      inv.email ||
      inv.phoneNumber
    );
  return inv.email || inv.phoneNumber || inv.id;
}

function whatsappDigits(phoneNumber) {
  return String(phoneNumber || "").replace(/\D/g, "");
}

function perPersonMessage(inv) {
  const name = invitationName(inv);
  const intro =
    customMessage.value.trim() ||
    t("groupImport.defaultMessage", {
      group: groupName.value || t("groupImport.yourGroup"),
    });
  return `${intro}${name ? ` (${name})` : ""}`;
}

function waLink(inv) {
  const digits = whatsappDigits(inv.phoneNumber);
  return `https://wa.me/${digits}?text=${encodeURIComponent(
    perPersonMessage(inv) + "\n" + inv.invitationUrl,
  )}`;
}

function openWhatsApp(inv) {
  window.open(waLink(inv), "_blank");
}

function shareInvitation(inv) {
  socialShare({
    subject: t("groupImport.shareSubject", {
      group: groupName.value || t("groupImport.yourGroup"),
    }),
    message: perPersonMessage(inv),
    url: inv.invitationUrl,
  });
}

async function copyInvitationLink(inv) {
  await copyText(inv.invitationUrl);
  f7.toast.show({ text: t("groupImport.linkCopied"), closeTimeout: 1500 });
}

async function copyAllLinks() {
  const text = sentInvitations.value.map((inv) => inv.invitationUrl).join("\n");
  await copyText(text);
  f7.toast.show({ text: t("groupImport.allLinksCopied"), closeTimeout: 1500 });
}

async function copyBulkMessage() {
  await copyText(bulkMessage.value);
  f7.toast.show({ text: t("groupImport.bulkCopied"), closeTimeout: 1500 });
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
  } catch {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy");
    document.body.removeChild(textarea);
  }
}

function goSource() {
  step.value = "SOURCE";
}

function resetParseState() {
  parseError.value = "";
  headers.value = [];
  sourceRows.value = [];
  sheetNames.value = [];
  workbook.value = null;
  previewRows.value = [];
  showAllPreview.value = false;
}

function resetAll() {
  step.value = "SOURCE";
  fileName.value = "";
  importResult.value = null;
  selectedIds.value = [];
  customMessage.value = "";
  expiresInDays.value = 14;
  sentInvitations.value = [];
  manualMembers.value = [createManualMember()];
  Object.assign(mapping, {
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
  });
  resetParseState();
}

function downloadTemplate() {
  const rows = [
    {
      firstName: "Maria",
      lastName: "Kamau",
      phoneNumber: "+32470123456",
      email: "maria@example.com",
    },
  ];
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Members");
  XLSX.writeFile(workbook, "avocash-members-template.xlsx");
}
</script>

<style scoped>
.upload-zone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 28px 16px;
  margin-top: 16px;
  border: 2px dashed #b8c4d2;
  border-radius: 16px;
  text-align: center;
}

.upload-zone span {
  color: var(--f7-on-surface-variant);
  font-size: 13px;
}

.hidden-input {
  display: none;
}

.selected-file {
  padding-top: 12px;
  color: var(--f7-theme-color);
  font-size: 13px;
}

.validation-errors {
  font-size: 12px;
  padding: 4px 4px 0;
}

.import-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  text-align: center;
}

.import-summary div {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.import-summary strong {
  font-size: 22px;
}

.import-summary span {
  color: var(--f7-on-surface-variant);
  font-size: 12px;
}

.invite-count {
  font-size: 13px;
  font-weight: 600;
  padding: 8px 4px 4px;
}

.share-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 4px 0 0;
}

.bulk-message {
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 12px;
  line-height: 1.5;
  background: var(--f7-block-strong-bg-color, #f5f5f5);
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 12px;
}

.margin-top {
  margin-top: 12px;
}
</style>
